import type { ComponentProps } from '@enonic/react-components'
import * as React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import type { Swiper as SwiperType, NavigationOptions } from 'swiper/types'
import 'swiper/css'
import { Section, Container, NewsItemCard, Icon } from '../primitives'
import type { NewsItemCardProps } from '../primitives'

export interface NewsListFeaturedData extends Record<string, unknown> {
  heading: string
  backgroundColor: 'none' | 'light-blue' | 'light-peach'
  articles: NewsItemCardProps[]
  link?: {
    href: string
    label: string
    newTab: boolean
  }
}

const bgMap: Record<NewsListFeaturedData['backgroundColor'], string> = {
  none: '',
  'light-blue': 'bg-slate-100',
  'light-peach': 'bg-peach-100',
}

const navButtonClass =
  'cursor-pointer flex h-12 w-12 shrink-0 items-center justify-center border border-blue-900 bg-white text-blue-900 transition-colors hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:border-slate-500 disabled:bg-transparent disabled:text-slate-500 focus-visible:outline-1 focus-visible:outline-slate-300'

export const NewsListFeatured = (props: ComponentProps) => {
  const { heading, backgroundColor, articles, link } =
    props.data as NewsListFeaturedData

  const prevRef = React.useRef<HTMLButtonElement | null>(null)
  const nextRef = React.useRef<HTMLButtonElement | null>(null)
  const swiperRef = React.useRef<SwiperType | null>(null)
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    if (!swiperRef.current) return
    const swiper = swiperRef.current
    const nav = swiper.params.navigation as NavigationOptions
    nav.prevEl = prevRef.current
    nav.nextEl = nextRef.current
    swiper.navigation.init()
    swiper.navigation.update()
    setReady(true)
  }, [])

  React.useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    const equalize = () => {
      const slides = Array.from(
        swiper.el.querySelectorAll<HTMLElement>('.swiper-slide'),
      )
      if (!slides.length) return
      slides.forEach((s) => {
        s.style.height = 'auto'
      })
      let max = 0
      slides.forEach((s) => {
        const h = s.getBoundingClientRect().height
        if (h > max) max = h
      })
      if (max > 0) {
        slides.forEach((s) => {
          s.style.height = max + 'px'
        })
      }
    }
    equalize()
    const ro = new ResizeObserver(equalize)
    ro.observe(swiper.el)
    return () => ro.disconnect()
  }, [articles])

  return (
    <Section background={bgMap[backgroundColor]}>
      <Container className="gap-y-8 md:gap-y-12 overflow-x-hidden">
        <div className="col-span-full flex items-center justify-between gap-4">
          {heading ? (
            <h2 className="text-3xl leading-tight text-blue-900 md:text-6xl md:leading-snug">
              {heading}
            </h2>
          ) : (
            <span className="sr-only" />
          )}
          <div
            className={`ml-auto flex shrink-0 gap-2 transition-opacity ${ready ? 'opacity-100' : 'opacity-0'}`}
          >
            <button
              ref={prevRef}
              type="button"
              aria-label="Forrige"
              className={navButtonClass}
            >
              <Icon name="arrow_back" size={24} />
            </button>
            <button
              ref={nextRef}
              type="button"
              aria-label="Neste"
              className={navButtonClass}
            >
              <Icon name="arrow_forward" size={24} />
            </button>
          </div>
        </div>

        <div className="col-span-full">
          <Swiper
            className="overflow-visible!"
            modules={[Navigation]}
            slidesPerView={1.1}
            spaceBetween={16}
            breakpoints={{
              768: { slidesPerView: 1.5, spaceBetween: 20 },
              1024: { slidesPerView: 2.2, spaceBetween: 20 },
            }}
            navigation={{ prevEl: null, nextEl: null }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
          >
            {articles.map((article) => (
              <SwiperSlide key={article.id}>
                <NewsItemCard {...article} headingLevel="h3" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {link && link.label && (
          <div className="col-span-full">
            <a
              href={link.href}
              className="text-xl leading-normal text-black underline decoration-slate-900"
              {...(link.newTab
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {link.label}
            </a>
          </div>
        )}
      </Container>
    </Section>
  )
}
